<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import ButtonQuantity from '@/components/buttons/ButtonQuantity.vue'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import { discountPriceFormatter } from '@/mixins/discountPriceFormatter'
import { numberToCurrency } from '@/mixins/numberToCurrency'
import { useI18n } from 'vue-i18n'
import { useModalStore } from '@/stores/ModalStore';
import { useAppStore } from '@/stores/AppStore'
import { useCountdown } from '@/composables/useCountdown'

import MiButton from '@/components/buttons/MiButton.vue'

const appStore = useAppStore()
const { getCurrentCustomer } = storeToRefs(appStore)
const { openModal } = useModalStore();

const { t } = useI18n()

const countdownTimer = ref<string>()

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  adaptive: {
    type: Boolean,
    default: false
  }
})

const showFromLabel = (quantityDiscounts: object[], plusClubPrice: string | null) => {
  return quantityDiscounts && (!plusClubPrice || !getCurrentCustomer.value?.inPlusClub)
}

const watchmanState = (action: string) => {
  switch (action) {
    case 'succeed': {
        return {
          severity: 'info',
          icon: 'heart-fill',
        }
    }
    case 'weather_too_hot': {
        return {
          severity: 'info',
          icon: 'frozen',
        }
    }
    case 'look_after': {
        return {
          severity: 'primary',
          icon: 'bird',
        }
    }
    default: {
        return {
            theme: 'primary',
            icon: 'bird',
        }
    }
  }
};

onMounted(() => {
  if (props.product.events && props.product.events[0].until) {
      useCountdown(props.product.events[0].until, (time) => {
          countdownTimer.value = `${time.days}${t('time_days')} ${time.hours}${t('time_hours')} ${time.minutes}${t('time_minutes')} ${time.seconds}${t('time_seconds')}`
      })
  }
})
</script>

<template>
  <article class="relative product-card-sm">
    <div class="flex flex-col">
      <div
        v-if="product.images.length > 0"
        class="relative px-2 py-1 rounded-lg bg-white"
      >
        <div
          v-if="product.events?.length && new Date(product.events?.[0]?.until)?.getTime() - new Date().getTime() > 0"
          class="absolute mt-1 ml-1 zindex-2"
        >
          <VTooltip theme="baseTooltip">
            <div
              class="event-badge flex items-center content-center rounded-pill p-1 flex text-lg mt-n2 ml-n2"
              :style="{ backgroundColor: product.events[0].backgroundColor }"
            >
              <img
                class="block w-100"
                :src="`https://img.mix.it/general_assets/categories/${product.events[0].icon}`"
                :alt="product.events[0].name"
              >
            </div>
            <template #popper>
              <span class="text-sm">
                {{ product.events[0].name }}
              </span><br>
              <strong class="text-lg">
                {{ product.events[0].text }}
              </strong>
              <br>
              <strong class="text-base mt-1">
                {{ t('event_ending_in') }} {{ countdownTimer }}
              </strong>
            </template>
          </VTooltip>
        </div>
        <RouterLink
          v-close-popper
          :to="{ name: 'productPath', params: { permalink: product.permalink } }"
        >
          <div :class="{'opacity-50': product.state !== 'available'}">
            <RemoteResponsiveImage
              :product-image-data="product.images[0]?.data"
              img-class="w-100"
              :alt="product.name"
              sizes="10vw"
            />
          </div>
        </RouterLink>
        <div class="absolute bottom-0 right-0 mr-1 mb-1">
          <ButtonQuantity
            v-if="!product.isIngredient && product.state === 'available'"
            :product="product"
          />
          <MiButton
            v-else-if="product.isIngredient && product.state === 'available'"
            v-close-popper
            severity="success"
            icon="mi-mix"
            size="sm"
            rounded
            aria-label="Add to mix"
            @click.prevent="
              openModal({
                id: 'push-to-mix-modal',
                params: {
                  permalink: product.permalink,
                  categories: product.mixableToCategories,
                },
              })
            "
          />
          <MiButton
            v-else-if="product.state !== 'hidden' || product.state !== 'removed' || product.state !== 'only_for_club'"
            v-close-popper
            :severity="watchmanState(product.state).severity"
            :icon="`mi-${watchmanState(product.state).icon}`"
            size="sm"
            rounded
            aria-label="Watchdog"
            @click="openModal({ id: 'modal-watchdog', params: { product } })"
          />
          <MiButton
            v-else
            v-close-popper
            severity="primary"
            icon="mi-bird"
            size="sm"
            rounded
            aria-label="Club"
            :link-to="{ name: 'clubPath' }"
          />
        </div>
      </div>
      <RouterLink
        v-close-popper
        :to="{ name: 'productPath', params: { permalink: product.permalink } }"
        class="no-decoration"
      >
        <h3 class="font-semi-bold text-primary mt-1 mb-1">
          {{ product.name }}
        </h3>
        <strong class="text-md text-primary">
          <template v-if="product.quantityDiscounts">
            {{
              discountPriceFormatter(
                showFromLabel(product.quantityDiscounts, product.plusClubPrice) ? t('from') : '',
                product.plusClubPrice,
                getCurrentCustomer?.inPlusClub || false,
                product.quantityDiscounts[0].price,
                product.quantityDiscounts[product.quantityDiscounts.length - 1].price,
                product.currency,
              )
            }}
          </template>
          <template v-else>{{ numberToCurrency(product.price, product.currency) }}</template>
        </strong>
      </RouterLink>
    </div>
  </article>
</template>

<style lang="scss" scoped>
.product-card-sm {
  width: 148px;

  h3 {font-size: 12px;line-height: 1.1rem;}

  @media screen and (max-width: 420px) {
    width: 160px;
  }
}

.event-badge {
  width: 32px;
  height: 32px;
}
</style>

<i18n>
  {
    "cs": {
    "event_ending_in": "Akce končí za",
    "from": "Od",
    "time_days": "d",
    "time_hours": "h",
    "time_minutes": "m",
    "time_seconds": "s"
},
    "sk": {
    "event_ending_in": "Akcia končí o",
    "from": "Od",
    "time_days": "d",
    "time_hours": "h",
    "time_minutes": "m",
    "time_seconds": "s"
},
    "pl": {
    "event_ending_in": "Promocja kończy się za",
    "from": "Od",
    "time_days": "dn.",
    "time_hours": "godz.",
    "time_minutes": "min.",
    "time_seconds": "sek."
},
    "hu": {
    "event_ending_in": "Az akció véget ér",
    "from": "A  címről",
    "time_days": "d",
    "time_hours": "h",
    "time_minutes": "m",
    "time_seconds": "s"
},
    "de": {
    "event_ending_in": "Die Aktion endet in",
    "from": "Von",
    "time_days": "d",
    "time_hours": "h",
    "time_minutes": "m",
    "time_seconds": "s"
},
    "at": {
    "event_ending_in": "Die Aktion endet in",
    "from": "Von",
    "time_days": "d",
    "time_hours": "h",
    "time_minutes": "m",
    "time_seconds": "s"
},
    "ro": {
    "event_ending_in": "Promoția se încheie în",
    "from": "De la",
    "time_days": "",
    "time_hours": "",
    "time_minutes": "",
    "time_seconds": "cu"
}
  }
</i18n>